import { LocalStorageType } from "./enums";
import { Platform } from "./interfaces";

/**
 * Device font-size scaling (iOS Dynamic Type), web-only.
 *
 * WKWebView has no `adjustsFontForContentSizeCategory` and ignores
 * `text-size-adjust`, so the reader's system font-size does not reach our web
 * UI. The only thing WebKit resolves by itself are the `-apple-system-*` text
 * styles, so we use `font: -apple-system-body` as a measuring stick: its
 * resolved size divided by 17 (the size at the default Dynamic Type category
 * "Large") is the scale factor. Nothing native is involved.
 *
 * A sentinel font-size on the probe distinguishes "WebKit resolved the
 * shorthand" from "the engine dropped the declaration". Without it, Chromium /
 * Gecko / Android WebView would leave the probe at the inherited 16px and we
 * would compute 16/17 and shrink the whole UI outside iOS.
 *
 * The factor is applied by `--device-font-scale`, which the root font-size
 * multiplies (clamped) in globals-redesign-base.sass and globals.sass.
 */

export const DEVICE_FONT_SCALE_PROPERTY = "--device-font-scale";

/** Dynamic Type body size at the default category, in CSS px. */
const BASELINE_PX = 17;

/** A size no text style resolves to, used to detect an unsupported engine. */
const SENTINEL_PX = 123;

/** Set by the pre-paint script; read by the gate so it does not re-measure. */
const MEASURED_ATTRIBUTE = "data-device-font-scale-measured";

/** Set to "override" when ?fontscale= won, so the gate keeps its hands off. */
const SOURCE_ATTRIBUTE = "data-device-font-scale-source";

const QUERY_PARAM = "fontscale";

/**
 * Measures the system-resolved `-apple-system-body` size and derives the
 * factor. Returns exactly 1 on engines that do not support the shorthand.
 * Unclamped — the bounds live in CSS next to the layout they protect.
 */
export function measureDeviceFontScale(): number {
  const root = document.documentElement;
  const probe = document.createElement("span");
  probe.setAttribute("aria-hidden", "true");
  probe.style.cssText = "position:absolute;visibility:hidden;pointer-events:none";
  // Order matters: the sentinel first, then the shorthand that overrides it
  // only where the engine understands it.
  probe.style.fontSize = `${SENTINEL_PX}px`;
  probe.style.font = "-apple-system-body";
  root.appendChild(probe);
  const measured = parseFloat(window.getComputedStyle(probe).fontSize);
  root.removeChild(probe);

  if (!isFinite(measured) || measured <= 0 || Math.round(measured) === SENTINEL_PX) {
    return 1;
  }
  return measured / BASELINE_PX;
}

/** The factor the pre-paint script measured, or a fresh measurement. */
export function getMeasuredDeviceFontScale(): number {
  const stashed = parseFloat(
    document.documentElement.getAttribute(MEASURED_ATTRIBUTE) || ""
  );
  return isFinite(stashed) && stashed > 0 ? stashed : measureDeviceFontScale();
}

/** True when ?fontscale= forced a factor — it wins over the platform gate. */
export function hasDeviceFontScaleOverride(): boolean {
  return document.documentElement.getAttribute(SOURCE_ATTRIBUTE) === "override";
}

/**
 * Applies the measured factor for iOS and clears it for every other platform;
 * persists the platform so the pre-paint script can apply it on later loads
 * without waiting for the native `window.setAppInfo` callback.
 */
export function applyDeviceFontScaleForPlatform(platform: Platform): void {
  if (typeof document === "undefined" || hasDeviceFontScaleOverride()) return;

  const root = document.documentElement;
  try {
    window.localStorage.setItem(LocalStorageType.Platform, platform);
  } catch {
    // private mode / storage full: we simply lose the pre-paint fast path
  }

  if (platform === Platform.iOS) {
    root.style.setProperty(DEVICE_FONT_SCALE_PROPERTY, String(getMeasuredDeviceFontScale()));
  } else {
    // Covers a stale cached platform, e.g. the same profile reporting Android now.
    root.style.removeProperty(DEVICE_FONT_SCALE_PROPERTY);
  }
}

/**
 * The pre-paint script, inlined in the `<head>` of both the App Router layout
 * and the Pages Router document. Must stay self-contained, synchronous and
 * dependency-free: it runs before `<body>` exists and before any bundle loads,
 * so the scale is already in place at the first paint and text does not visibly
 * jump. Keep the literals in sync with the constants above — they cannot be
 * imported into an inline script.
 */
export const DEVICE_FONT_SCALE_INLINE_SCRIPT = `(function(){try{
var r=document.documentElement,P='${DEVICE_FONT_SCALE_PROPERTY}',S=${SENTINEL_PX},B=${BASELINE_PX};
var m=/[?&]${QUERY_PARAM}=([^&]*)/.exec(window.location.search||'');
if(m){var o=parseFloat(decodeURIComponent(m[1]));if(isFinite(o)&&o>0){
r.setAttribute('${SOURCE_ATTRIBUTE}','override');r.style.setProperty(P,String(o));return}}
var p=document.createElement('span');p.setAttribute('aria-hidden','true');
p.style.cssText='position:absolute;visibility:hidden;pointer-events:none';
p.style.fontSize=S+'px';p.style.font='-apple-system-body';
r.appendChild(p);var v=parseFloat(window.getComputedStyle(p).fontSize);r.removeChild(p);
var s=(isFinite(v)&&v>0&&Math.round(v)!==S)?v/B:1;
r.setAttribute('${MEASURED_ATTRIBUTE}',String(s));
/* '${LocalStorageType.Platform}' mirrors LocalStorageType.Platform in helper/enums.ts */
var c=null;try{c=window.localStorage.getItem('${LocalStorageType.Platform}')}catch(e){}
if(c==='${Platform.iOS}'){r.style.setProperty(P,String(s))}
}catch(e){}})()`;
